import _ from "lodash";
import React, { useState, useCallback } from "react";
import styled from "styled-components";
import JSONApi from "../../utils/json_api";
import { useMedia } from "react-media";
import { useSelector, useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQueryCache, useQuery } from "react-query";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import { Checkbox, Divider, Grid, Button, Icon, Header, Segment, Form } from "semantic-ui-react";
import { Controller, FormProvider, useForm, useFormContext } from "react-hook-form";
import { useClient } from "../../hooks/use-client";
import { ControlledInput, ControlledTextArea } from "../ControlledForm";
import { AddressFormField } from "../AddressFormField";
import { ControlledCrmTopicSelect } from "../CrmTopicSelect";
import { ControlledEmployeeSelect } from "../EmployeeSelect";
import FileUploader from "../../components/FileUploader";
import { postCrmRequest } from "../../queries/crmRequests";
import { fetchGetCrmTopics } from "../../queries/crmTopics";
import AppSettings from "../../config";
import networkSelectors from "../../selectors/network";

import "./New.scss";

const googleLibraries = ["places"] as any;
const StyledContactForm = styled.div`
  padding: 10px;
  padding-left: 25px;
  background-color: #a5a9b3;

  .ui.header {
    color: white;
    text-transform: uppercase;
  }

  &&&& {
    .ui.toggle.checkbox label {
      color: white !important;
    }

    label {
      color: white;
      font-size: 12px;
    }
  }
`;

const StyledRequestForm = styled.div`
  padding: 10px;
  padding-left: 25px;
`;

const ContactForm = () => {
  const { control } = useFormContext();
  const [anonymous, setAnonymous] = useState(true);
  return (
    <Form as='div' size='tiny'>
      <Header>Customer Information</Header>
      <Form.Group>
        <Checkbox toggle label='Check for Anonymous' checked={anonymous} onClick={() => setAnonymous(!anonymous)} />
      </Form.Group>
      {!anonymous && (
        <Grid>
          <Grid.Column width={10}>
            <Form.Group>
              <Form.Field required>
                <ControlledInput label='Last Name' control={control} name='lastName' placeholder='Doe' />
              </Form.Field>
              <Form.Field>
                <ControlledInput label='First Name' control={control} name='firstName' placeholder='John' />
              </Form.Field>
              <Form.Field>
                <ControlledInput label='Phone' control={control} name='phone' placeholder='(xxx) xxx-xxxx' />
              </Form.Field>
              <Form.Field required>
                <ControlledInput label='Email' control={control} name='email' placeholder='john@example.com' />
              </Form.Field>
            </Form.Group>
          </Grid.Column>
          <Grid.Column width={6}>
            <Form.Group>
              <Form.Field>
                <label htmlFor='address'>Address</label>
                <AddressFormField id='address' />
              </Form.Field>
            </Form.Group>
          </Grid.Column>
        </Grid>
      )}
    </Form>
  );
};

const RequestForm = ({ defaultCenter }: any) => {
  const dispatch = useDispatch();
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: AppSettings.GOOGLE_MAP_KEY,
    libraries: googleLibraries,
  });
  const { control, errors, watch } = useFormContext();
  const [autocomplete, setAutocomplete] = useState<any>();
  // const enteredViaOptions = [{key: 'phone', value: 'Phone', text: 'Phone'}, { key: 'email', value: 'Email', text: 'Email' }]
  const topicsQuery = useQuery(["crmTopics"], fetchGetCrmTopics(dispatch));
  const topicId = watch("topicId");
  const selectedTopic = JSONApi.findById(_.get(topicsQuery.data, "payload.data", []), topicId);
  const showLocation = _.get(selectedTopic, "attributes.location") != null;
  const isLocationRequired = _.get(selectedTopic, "attributes.location") === "required";
  document.title = "New CRM Request";
  return (
    <Form as='div' size='tiny'>
      <Form.Group>
        <Form.Field required inline>
          <label htmlFor='topic'>Topic</label>
          <ControlledCrmTopicSelect id='topic' search name='topicId' control={control} />
        </Form.Field>
      </Form.Group>
      {showLocation && (
        <Form.Group>
          <Form.Field inline required={isLocationRequired}>
            <label htmlFor='location'>Location</label>
            <Controller
              control={control}
              name='location'
              render={({ onChange }) => {
                if (!isLoaded) {
                  return <p>Loading...</p>;
                }
                const bounds = new google.maps.Circle({
                  center: defaultCenter,
                  radius: 1,
                }).getBounds();
                return (
                  <>
                    <Autocomplete
                      className='Autocomplete'
                      bounds={bounds!}
                      options={{
                        types: ["address"],
                      }}
                      onLoad={(mod: any) => setAutocomplete(mod)}
                      onPlaceChanged={() => {
                        const place = autocomplete.getPlace();
                        const shortAddress = place.address_components
                          .filter((c: any) => !_.isEmpty(_.intersection(c.types, ["street_number", "route"])))
                          .map((c: any) => c.short_name)
                          .join(" ");
                        const locationData = {
                          address: place.formatted_address,
                          shortAddress: shortAddress,
                          coordinates: {
                            latitude: place.geometry.location.lat(),
                            longitude: place.geometry.location.lng(),
                          },
                          result: place,
                        };
                        onChange({
                          adapter: "google",
                          data: locationData,
                        });
                      }}
                    >
                      <Form.Input id='location' placeholder='' style={{ width: "300px" }} />
                    </Autocomplete>
                  </>
                );
              }}
            />
          </Form.Field>
        </Form.Group>
      )}
      <Form.Group>
        <Form.Field inline>
          <label htmlFor='assigned-to'>Assigned To</label>
          <ControlledEmployeeSelect
            id='assigned-to'
            search
            clearable
            placeholder='Leave blank for automatic routing'
            name='assignedToId'
            control={control}
          />
        </Form.Field>
      </Form.Group>
      <Form.Group width={16}>
        <Form.Field required width={16}>
          <ControlledTextArea
            style={{ width: "100%" }}
            name='description'
            label='Description'
            control={control}
            placeholder='Describe the problem. Customer will see this field'
            errors={errors}
            rows={5}
          />
        </Form.Field>
      </Form.Group>
      <Form.Group width={16}>
        <Form.Field width={16}>
          <label htmlFor='attachments'>Attachments</label>
          <Controller
            control={control}
            name='attachments'
            render={({ onChange, value }) => (
              <>
                <FileUploader
                  baseUrl={AppSettings.ATTACHMENTS_URL}
                  file={null}
                  setFile={(file: any) => {
                    onChange([...value, file]);
                    value.push(file);
                  }}
                  uploadId='file'
                  placeholder={
                    <div
                      style={{
                        display: "block",
                        border: "1px dotted gray",
                        width: "100%",
                        padding: "10px",
                        cursor: "pointer",
                        textAlign: "center",
                      }}
                    >
                      <p>
                        <Icon name='plus' color='grey' />
                        Click here or Drag files to add
                      </p>
                    </div>
                  }
                />
                {value.map((file: any, index: number) => (
                  <p key={file.name}>
                    <Icon name='check' color='green' />
                    {file.name}
                    <Icon
                      name='trash'
                      color='red'
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        const newValue = _.filter(value, (v: any, i: number) => i !== index);
                        onChange(newValue);
                      }}
                    />
                  </p>
                ))}
              </>
            )}
          />
        </Form.Field>
      </Form.Group>
    </Form>
  );
};

interface IRequest {
  topicId: number;
  howEntered: string;
  description: string;
  assignedToId: number;
  location: { adapter: string; data: any };
  attachments: Array<any>;
}

export const CrmNew = () => {
  const isSmallScreen = useMedia({ query: "(max-width: 640px)" });
  const params = useParams<{ site: string }>();
  const client = useClient(params.site);
  const crmRequestForm = useForm({
    defaultValues: {
      attachments: [],
    },
  });
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const queryCache = useQueryCache();
  const [mutate] = useMutation(postCrmRequest(networkHeaders), {
    onSuccess: () => queryCache.invalidateQueries("crmRequests"),
  });

  const onSave = useCallback(async (request: IRequest) => {
    const response = await mutate({
      topicId: request.topicId,
      description: request.description,
      assignedToId: request.assignedToId,
      location: request.location,
      attachments: request.attachments.map((attachment: any) => ({
        ...attachment,
        internal: true,
      })),
    });
    const requestId = _.get(response, "data.id");
    if (requestId) {
      const pageWhenDone = `${window.location.origin}/${params.site}/crm`;
      const legacyUrl = `${AppSettings.GOGOV_WEB_URL}/${params.site}/editcase.php?cmd=edit&pagewhendone=${pageWhenDone}&id=${requestId}`;
      const legacyMobileUrl = `${AppSettings.GOGOV_MOBILE_URL}/${params.site}/editcase.php?cmd=edit&pagewhendone=${pageWhenDone}&id=${requestId}`;
      window.location.href = isSmallScreen ? legacyMobileUrl : legacyUrl;
    }
  }, []);
  const formLeft = (
    <Link to={`/${params.site}/crm`}>
      <Icon name='long arrow alternate left' />
      Return to List
    </Link>
  );

  return (
    <FormProvider {...crmRequestForm}>
      <Header style={{ backgroundColor: "#505870", color: "white", margin: 0, padding: "21px" }}>New Request</Header>
      <StyledContactForm>
        <ContactForm />
      </StyledContactForm>
      <StyledRequestForm>
        <RequestForm defaultCenter={_.get(client.query, "data.payload.data.attributes.default-center")} />
      </StyledRequestForm>
      <Segment basic>
        <Divider />
        <Grid columns='equal'>
          <Grid.Column>{formLeft}</Grid.Column>
          <Grid.Column textAlign='right'>
            <Button primary onClick={crmRequestForm.handleSubmit(onSave)}>
              Save
            </Button>
          </Grid.Column>
        </Grid>
      </Segment>
    </FormProvider>
  );
};

export default CrmNew;
